import {
  Alert,
  Badge,
  Button,
  Col,
  Dialog,
  Dropdown,
  FormContainer,
  FormItem,
  Input,
  Link,
  RemoveRedEyeOutlinedIcon,
  Row,
  Spinner,
} from 'elmo-elements';
import React, { useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import PlayerUi from '@/components/Player';
import { AiSparkleIcon, ImageOutlineIcon } from '@/components/Icons';
import { PPT_IMPORT_HELP_URL } from '@/modules/services/pptImport';
import { AI_LEARN_MORE_URL } from '@/components/AiCaveatNotice';
import config from '@/lib/config';
import { appData } from '@/lib/data';
import {
  getCurrentFrame,
  getLocalstorage,
  hasVocamData,
  setLocalstorage,
} from '@/lib/util';
import {
  useGetElearningBehaviourSettingsQuery,
  useGetElearningQuery,
  useGetFramesQuery,
  useGetHelperContentQuery,
  useGetHelperFaqQuery,
  useGetResourcesQuery,
  useGetVideoLinkQuery,
} from '@/modules/services';
import { FrameDataType } from '@/modules/types/frame';
import FrameContentStyleIsolator from '@/modules/utils/FrameContentStyleIsolator';
import Canvas from '@/pages/Module/Slide/components/Canvas';
import { ElearningParamType } from '@/pages/Module/routes';

import VideoSlideCanvas from '@/pages/Module/Slide/components/VideoSlideCanvas';
import { ContentFormType, SlideOp, SLIDE_OPS } from '../SlideForm';
import './Content.scss';

interface ContentPropsType {
  form: UseFormReturn<ContentFormType, object>;
  frameData?: FrameDataType;
  browseAssetLibraryHandler: () => void;
  setIsVideoLibraryModalOpen: (isOpen: boolean) => void;
  setIsMobileMode: React.Dispatch<boolean>;
  isMobileMode: boolean;
  isPptxImport?: boolean;
  isAiGenerated?: boolean;
  isGuardrailBlocked?: boolean;
  isGenerationFailed?: boolean;
  isReplacedWithImage?: boolean;
  showEditWithAi?: boolean;
  showReplaceWithImage?: boolean;
  activeSlideOp?: SlideOp | null;
  onEditWithAi?: () => void;
  onReplaceWithImage?: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

const Content = ({
  frameData,
  form,
  setIsVideoLibraryModalOpen,
  browseAssetLibraryHandler,
  isMobileMode,
  setIsMobileMode,
  isPptxImport = false,
  isAiGenerated = false,
  isGuardrailBlocked = false,
  isGenerationFailed = false,
  isReplacedWithImage = false,
  showEditWithAi = false,
  showReplaceWithImage = false,
  activeSlideOp = null,
  onEditWithAi,
  onReplaceWithImage,
  onDirtyChange,
}: ContentPropsType): React.ReactElement => {
  const { elearningId, frameId } = useParams<ElearningParamType>();
  const { data: elearningBehaviourSettingsData } =
    useGetElearningBehaviourSettingsQuery();

  const elearning = useGetElearningQuery(elearningId);
  const frames = useGetFramesQuery({ elearningId });
  const helperContent = useGetHelperContentQuery('');
  const helperFaq = useGetHelperFaqQuery('');
  const resources = useGetResourcesQuery({ elearningId });

  const [showMobileToolbar, setShowMobileToolbar] = useState(false);
  // Per-slide, per-browser-session dismissal (LMS2-5107). sessionStorage can throw
  // in private windows, so a failed read just shows the banner again.
  const aiBannerKey = `ai-banner-dismissed:${frameData?.id}`;
  const [isAiBannerDismissed, setIsAiBannerDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(aiBannerKey) === '1';
    } catch {
      return false;
    }
  });
  const dismissAiBanner = () => {
    setIsAiBannerDismissed(true);
    try {
      sessionStorage.setItem(aiBannerKey, '1');
    } catch {
      // ignore
    }
  };
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const currentFrame = useMemo(
    () => getCurrentFrame(frames.data ?? [], frameId ?? ''),
    [frames.data, frameId],
  );
  const { data: vocamVideoLink } = useGetVideoLinkQuery(frameId ?? '', {
    skip: !frameId || !hasVocamData(currentFrame),
  });
  useEffect(() => {
    if (frames && frameId && elearningId) {
      const content = currentFrame?.content;
      const mobileContent = currentFrame?.mobileContent;

      setLocalstorage(
        'PreviewContent',
        {
          elearningId,
          frameId,
          content,
          mobileContent,
        },
        'object',
      );
    }
  }, [frames, frameId, elearningId]);

  // Need this effect to validate unsaved changes on the mobile content
  useEffect(() => {
    const { mobileContent: storageMobileContent } = getLocalstorage(
      'PreviewContent',
      'object',
    );

    setHasUnsavedChanges(
      !!currentFrame?.mobileContent &&
        currentFrame?.mobileContent !== storageMobileContent,
    );
  }, [currentFrame?.mobileContent]);

  const {
    register,
    formState: { errors },
    setValue,
    watch,
  } = form;

  const mobileContent = watch('mobileContent');
  const desktopContent = watch('content');

  const handlePreview = (isMobilePreview: boolean) => {
    const windowMetadata = elearning.data?.theme
      ? elearning.data.theme.windowMetadata
      : appData.playerTheme.default.windowMetadata;
    const mobileWidth = 400;
    const mobileHeight = 675;

    if (windowMetadata) {
      window.PREVIEW_WINDOW = window.open(
        `/preview/${elearning.data?.id}/${
          frameId || ''
        }?mobile=${isMobilePreview}`,
        'preview',
        `width=${
          isMobilePreview ? mobileWidth : windowMetadata.width + 5
        },height=${
          isMobilePreview ? mobileHeight : windowMetadata.height + 10
        }, location=0,status=0, scrollbars=0, toolbar=0,menubar=0,resizable=1`,
      );
    }
  };

  const previewSetLocalstorage = (previewContent?: string) => {
    setLocalstorage(
      'PreviewContent',
      {
        elearningId,
        frameId,
        content: previewContent,
      },
      'object',
    );
  };

  const mobilePreviewSetLocalstorage = (previewMobileContent?: string) => {
    setLocalstorage(
      'PreviewContent',
      {
        elearningId,
        frameId,
        content: desktopContent,
        mobileContent: previewMobileContent,
      },
      'object',
    );
  };

  const handleEditDesktopButtonClick = () => {
    if (hasUnsavedChanges) {
      Dialog.confirm({
        title: 'Unsaved changes',
        content:
          'You have unsaved changes in the mobile version. Are you sure you want to leave this view?',
        okLabel: 'Continue',
        cancelLabel: 'Stay on mobile version',
        onOk() {
          setIsMobileMode(false);
          setValue('mobileContent', currentFrame?.mobileContent ?? '');

          // Discard mobile changes on local storage to fix the preview
          mobilePreviewSetLocalstorage(currentFrame?.mobileContent ?? '');
        },
      });
    } else {
      setIsMobileMode(false);
    }
  };

  const handleEditMobileButtonClick = () => {
    setIsMobileMode(true);
    setValue('mobileContent', currentFrame?.mobileContent || desktopContent);
  };

  const onCanvasChange = (e: any) => {
    const editorContent = e.editor.getData();
    previewSetLocalstorage(editorContent);
    setValue('content', editorContent);
    // Only flag unsaved changes for genuine user edits, which happen while the
    // editor is focused. Programmatic changes on load (e.g. CKEditor's HTML
    // re-serialization and FrameContentStyleIsolator rewriting embedded <style>
    // rules) fire change events without focus and must not mark the slide dirty.
    if (e.editor.focusManager?.hasFocus) {
      onDirtyChange?.(e.editor.checkDirty());
    }
    FrameContentStyleIsolator();
  };

  const onMobileCanvasChange = (e: any) => {
    const editorContent = e.editor.getData();
    const currentMobileContent = currentFrame?.mobileContent;

    setHasUnsavedChanges(editorContent !== currentMobileContent);
    mobilePreviewSetLocalstorage(editorContent);
    setValue('mobileContent', editorContent);
    FrameContentStyleIsolator();
  };

  const onCanvasLoad = () => {
    FrameContentStyleIsolator();
  };

  const onMobileCanvasLoad = () => {
    FrameContentStyleIsolator();
  };

  const onInputChange = (e: any) => {
    const value = e.target ? e.target.value : e;
    setValue('title', value);
  };

  const playerHelper = {
    content: helperContent.data,
    faq: helperFaq.data,
  };

  const browseVocamVideoHandler = () => {
    setIsVideoLibraryModalOpen(true);
  };

  const getCanvas = (): React.ReactElement => {
    const isVideoCanvas =
      frameData?.isVideoFrame &&
      !frameData?.bgVideo &&
      !hasVocamData(frameData);

    if (isVideoCanvas && elearningBehaviourSettingsData) {
      return (
        <VideoSlideCanvas
          browseAsset={browseAssetLibraryHandler}
          browseVocamVideo={browseVocamVideoHandler}
          hasVideoLibrary={elearningBehaviourSettingsData.videoLibraryEnabled}
        />
      );
    }

    const frameContent = frameData
      ? {
          ...frameData,
          content: desktopContent,
          mobileContent,
        }
      : frameData;

    const canvasProps = {
      frameContent,
      elearningBehaviourSettings: elearningBehaviourSettingsData,
      vocamVideoLink,
      key: `Canvas-${frameData?.id}`,
      isMobile: isMobileMode,
      onChange: isMobileMode ? onMobileCanvasChange : onCanvasChange,
      onLoad: isMobileMode ? onMobileCanvasLoad : onCanvasLoad,
      onFocus: isMobileMode ? () => setShowMobileToolbar(true) : () => {},
      onBlur: isMobileMode ? () => setShowMobileToolbar(false) : () => {},
    };

    return <Canvas {...canvasProps} />;
  };

  if (!elearning.data) {
    return <Spinner />;
  }

  const canvas = getCanvas();

  return (
    <>
      <textarea
        id="SlideContent"
        data-testid="textarea-SlideContent"
        {...register('content', {})}
        value={watch('content')}
        style={{ visibility: 'hidden', display: 'none' }}
      />
      <textarea
        id="MobileSlideContent"
        data-testid="textarea-MobileSlideContent"
        {...register('mobileContent', {})}
        value={watch('mobileContent')}
        style={{ visibility: 'hidden', display: 'none' }}
      />
      {isPptxImport && !isGuardrailBlocked && !isReplacedWithImage && (
        <Alert
          type="info"
          isCloseable={true}
          heading="Converted by AI."
          className="mb-3"
        >
          Your PowerPoint content was converted into an editable format using
          AI. Please review to ensure accuracy and appropriateness.{' '}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link url={PPT_IMPORT_HELP_URL} newWindow>
            Learn more
          </Link>
        </Alert>
      )}
      {isAiGenerated && !isAiBannerDismissed && (
        <Alert
          type="info"
          isCloseable={true}
          onClose={dismissAiBanner}
          heading="Created by AI."
          className="mb-3"
        >
          This slide was generated by AI. Please review it for accuracy and
          appropriateness before publishing.{' '}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link url={AI_LEARN_MORE_URL} newWindow>
            Learn more
          </Link>
        </Alert>
      )}
      <FormContainer maxColumns={2}>
        <FormItem
          label="Slide Name*"
          isBlock={true}
          message={errors.title?.message}
          status={errors.title ? 'error' : 'default'}
        >
          <Input
            id="SlideName"
            placeholder="Slide Name"
            value={watch('title')}
            {...register('title', {})}
            onChange={onInputChange}
          />
        </FormItem>
      </FormContainer>

      <div
        style={{
          width: elearning.data?.theme?.windowMetadata?.width ?? '1024px',
          position: 'relative',
        }}
        className="slide-content-container"
      >
        <FormItem
          isBlock={true}
          message={errors.content?.message}
          status={errors.content ? 'error' : 'default'}
        >
          <div>
            <div id="CKEditorToolbar" />

            {!isMobileMode && (
              <PlayerUi
                isPlayable={false}
                currentFrameId={frameId || ''}
                isQuizEnabled={false}
                elearning={elearning.data}
                frames={frames.data || []}
                resources={resources.data || []}
                helperContent={playerHelper.content || []}
                helperFaq={playerHelper.faq || []}
                vocamVideoLink={vocamVideoLink}
                canvas={canvas}
                elearningBehaviourSettings={elearningBehaviourSettingsData}
              />
            )}
            {isMobileMode && (
              <div
                style={{
                  background: `url(${config.assetPath}device-frame.png)`,
                  position: 'relative',
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                  width: 428,
                  height: 885,
                  zIndex: 1,
                  margin: '0 auto',
                  padding: '53px 32px 0px 32px',
                }}
              >
                <PlayerUi
                  isPlayable={false}
                  currentFrameId={frameId || ''}
                  isQuizEnabled={false}
                  elearning={elearning.data}
                  frames={frames.data || []}
                  resources={resources.data || []}
                  helperContent={playerHelper.content || []}
                  helperFaq={playerHelper.faq || []}
                  isMobileView={isMobileMode}
                  vocamVideoLink={vocamVideoLink}
                  canvas={canvas}
                  elearningBehaviourSettings={elearningBehaviourSettingsData}
                />
              </div>
            )}
          </div>
        </FormItem>
        {frameId !== 'add' && (
          <div>
            <Row
              wrap="nowrap"
              direction="row"
              justifyContent="between"
              className="slide-toolbar-row"
            >
              <Col className="slide-toolbar-left">
                {!isGuardrailBlocked && !isGenerationFailed && (
                  <Dropdown
                    text={
                      <>
                        <RemoveRedEyeOutlinedIcon />
                        Preview Slide
                      </>
                    }
                    ariaLabel="Add Slide/Section Title"
                    icon={<div />}
                    className="slide-preview-dropdown"
                  >
                    <Dropdown.Item
                      ariaLabel="preview desktop version"
                      value="preview-desktop"
                      onClick={() => {
                        handlePreview(false);
                      }}
                    >
                      Desktop
                    </Dropdown.Item>
                    <Dropdown.Item
                      ariaLabel="preview mobile version"
                      value="preview-mobile"
                      onClick={() => {
                        handlePreview(true);
                      }}
                    >
                      Mobile
                    </Dropdown.Item>
                  </Dropdown>
                )}
                {isGuardrailBlocked || isGenerationFailed ? (
                  <Badge
                    type="warning"
                    role="status"
                    className="slide-pptx-badge"
                  >
                    {isGuardrailBlocked
                      ? 'AI safety filter'
                      : 'Conversion failed'}
                  </Badge>
                ) : (
                  (isPptxImport || isAiGenerated) && (
                    <Badge
                      type="grey"
                      role="status"
                      className="slide-pptx-badge"
                      icon={<span className="slide-pptx-badge__dot" />}
                    >
                      {isPptxImport ? 'PowerPoint' : 'Created by AI'}
                    </Badge>
                  )
                )}
              </Col>
              <Col className="slide-toolbar-right">
                {showEditWithAi && (
                  <Button
                    id="SlideToolbarEditWithAi"
                    type="default"
                    isUppercase={false}
                    isDisabled={activeSlideOp !== null}
                    size="sm"
                    icon={
                      activeSlideOp === SLIDE_OPS.EDIT_AI ? (
                        <Spinner size="s" />
                      ) : (
                        <AiSparkleIcon size="sm" />
                      )
                    }
                    onClick={onEditWithAi}
                  >
                    Edit with AI
                  </Button>
                )}
                {showReplaceWithImage && (
                  <Button
                    id="SlideToolbarReplaceWithImage"
                    type="default"
                    isUppercase={false}
                    size="sm"
                    isDisabled={activeSlideOp !== null}
                    icon={
                      activeSlideOp === SLIDE_OPS.KEEP_AS_IMAGE ? (
                        <Spinner size="s" />
                      ) : (
                        <ImageOutlineIcon size="sm" />
                      )
                    }
                    onClick={onReplaceWithImage}
                  >
                    Replace with image
                  </Button>
                )}
                {isMobileMode ? (
                  <Button
                    className="slide-edit-btn"
                    type="default"
                    isUppercase={false}
                    onClick={handleEditDesktopButtonClick}
                    size="xl"
                  >
                    Edit Desktop
                  </Button>
                ) : (
                  <Button
                    className="slide-edit-btn"
                    type="default"
                    isUppercase={false}
                    onClick={handleEditMobileButtonClick}
                    size="xl"
                  >
                    Edit Mobile
                  </Button>
                )}
              </Col>
            </Row>
          </div>
        )}
      </div>
    </>
  );
};

export default Content;
